<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from "vue";
import CustomLoader from "~/components/ui/CustomLoader.vue";
import Button from "~/components/atoms/Button.vue";
import LOGO_TRANSPARENT from "~/assets/Logo - Transparent.png";
import { useQueryClient } from "@tanstack/vue-query";

const appState = useAppState();
const queryClient = useQueryClient();

const order = computed(() => appState.completedOrderData.value);
const canvasRef = ref<HTMLCanvasElement | null>(null);
const eyebrowRef = ref<HTMLElement | null>(null);
let animationFrameId: number | null = null;

function formatPrice(val?: number) {
  if (!val) return "";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
}

function handleClose() {
  appState.isPaymentSuccessModalOpen.value = false;
  queryClient.invalidateQueries({ queryKey: ["artworks"] });
  queryClient.invalidateQueries({ queryKey: ["orders"] });
  queryClient.invalidateQueries({ queryKey: ["cart"] });
}

function handleViewOrders() {
  handleClose();
  navigateTo("/orders");
}

function handleExploreArt() {
  handleClose();
  navigateTo("/artworks");
}

function handleShare() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://quietart.com";
  const shareText = encodeURIComponent(
    `I just acquired an original piece on Quiet Art Movement!\nExplore the collection: ${origin}`
  );
  window.open(`https://api.whatsapp.com/send?text=${shareText}`, "_blank");
}

// ── Constellation Stardust & Celestial Shimmer Animation ───────
interface SparkleParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  twinklePhase: number;
  twinkleSpeed: number;
  alpha: number;
  shape: "star" | "orb" | "diamond";
}

function draw4PointStar(ctx: CanvasRenderingContext2D, size: number) {
  const outer = size;
  const inner = size * 0.28;
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    ctx.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
    const innerAngle = angle + Math.PI / 4;
    ctx.lineTo(Math.cos(innerAngle) * inner, Math.sin(innerAngle) * inner);
  }
  ctx.closePath();
  ctx.fill();
}

function runCelebrationParticles() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const width = (canvas.width = canvas.offsetWidth * window.devicePixelRatio);
  const height = (canvas.height = canvas.offsetHeight * window.devicePixelRatio);
  ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

  let originX = 80;
  let originY = 65;
  let spreadWidth = 90;

  if (eyebrowRef.value && canvas) {
    const eyebrowRect = eyebrowRef.value.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    originX = eyebrowRect.left - canvasRect.left + eyebrowRect.width / 2;
    originY = eyebrowRect.top - canvasRect.top + eyebrowRect.height / 2;
    spreadWidth = eyebrowRect.width * 0.7;
  }

  const colors = ["#d85a30", "#eab308", "#fbbf24", "#f97316", "#0a0a0a", "#94a3b8"];
  const particles: SparkleParticle[] = [];
  const particleCount = 42;

  for (let i = 0; i < particleCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1.0 + Math.random() * 3.8;
    const shapeRoll = Math.random();
    particles.push({
      x: originX + (Math.random() - 0.5) * spreadWidth,
      y: originY + (Math.random() - 0.5) * 14,
      vx: Math.cos(angle) * speed + 0.6,
      vy: Math.sin(angle) * speed - 1.0,
      size: shapeRoll > 0.5 ? 6 + Math.random() * 6 : 2.5 + Math.random() * 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.08,
      twinklePhase: Math.random() * Math.PI * 2,
      twinkleSpeed: 3 + Math.random() * 4,
      alpha: 1,
      shape: shapeRoll > 0.4 ? "star" : (shapeRoll > 0.2 ? "diamond" : "orb"),
    });
  }

  let startTime = performance.now();

  function animate(now: number) {
    const elapsed = (now - startTime) / 1000;
    ctx?.clearRect(0, 0, width, height);

    let activeCount = 0;
    for (const p of particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.04; // delicate float gravity
      p.vx *= 0.985;
      p.rotation += p.rotationSpeed;

      // Smooth twinkle pulse
      const twinkle = 0.7 + 0.3 * Math.sin(elapsed * p.twinkleSpeed + p.twinklePhase);

      if (elapsed > 0.8) {
        p.alpha = Math.max(0, p.alpha - 0.015);
      }

      if (p.alpha > 0) {
        activeCount++;
        ctx.save();
        ctx.globalAlpha = p.alpha * twinkle;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 4;

        if (p.shape === "star") {
          draw4PointStar(ctx, p.size * twinkle);
        } else if (p.shape === "diamond") {
          ctx.beginPath();
          ctx.moveTo(0, -p.size);
          ctx.lineTo(p.size * 0.6, 0);
          ctx.lineTo(0, p.size);
          ctx.lineTo(-p.size * 0.6, 0);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }

    if (activeCount > 0) {
      animationFrameId = requestAnimationFrame(animate);
    }
  }

  animationFrameId = requestAnimationFrame(animate);
}

watch(
  () => [appState.isPaymentSuccessModalOpen.value, appState.isProcessingPayment.value],
  ([isOpen, isProcessing]) => {
    if (isOpen && !isProcessing) {
      setTimeout(() => {
        runCelebrationParticles();
      }, 120);
    } else {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    }
  }
);

onBeforeUnmount(() => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="payment-modal">
      <div
        v-if="appState.isPaymentSuccessModalOpen.value"
        class="fixed inset-0 z-105 flex items-center justify-center p-4 sm:p-6"
      >
        <!-- Backdrop with Blur -->
        <div
          class="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          @click="!appState.isProcessingPayment.value && handleClose()"
        />

        <!-- Modal Card -->
        <div
          class="relative w-full max-w-[460px] bg-primary rounded-[20px] shadow-[0_25px_70px_rgba(0,0,0,0.25)] border border-on-primary-border overflow-hidden z-10 text-left p-6 sm:p-8 flex flex-col items-start gap-5"
        >
          <!-- Confetti Canvas Overlay -->
          <canvas
            ref="canvasRef"
            class="pointer-events-none absolute inset-0 w-full h-full z-20"
          />

          <!-- Top Row: Logo on Top Left, Close on Top Right -->
          <div class="w-full flex items-center justify-between z-30">
            <img :src="LOGO_TRANSPARENT" alt="Quiet Art Movement" class="h-6 sm:h-7 object-contain" />
            <button
              v-if="!appState.isProcessingPayment.value"
              type="button"
              class="p-1.5 text-on-primary-body hover:text-on-primary-heading hover:bg-button-secondary rounded-full transition-colors cursor-pointer bg-transparent border-none flex items-center justify-center -mr-1"
              aria-label="Close"
              @click="handleClose"
            >
              <Icon name="lucide:x" class="size-4" />
            </button>
          </div>

          <!-- Processing State -->
          <div v-if="appState.isProcessingPayment.value" class="w-full flex flex-col items-center justify-center py-8 gap-5 z-10 text-center">
            <CustomLoader />
            <div class="flex flex-col gap-1.5">
              <h3 class="text-xl font-bold text-on-primary-heading">Finalizing Your Order</h3>
              <p class="text-xs sm:text-sm text-on-primary-body max-w-xs leading-relaxed">
                Please keep this window open while we securely verify payment and register your authenticated order receipt...
              </p>
            </div>
          </div>

          <!-- Success / Gratitude State -->
          <template v-else>
            <!-- Eyebrow & Title -->
            <div class="w-full flex flex-col gap-1.5 z-10 pt-1 text-left">
              <span
                ref="eyebrowRef"
                class="text-[11px] font-bold tracking-[0.2em] text-[#d85a30] uppercase"
              >
                ORDER CONFIRMED
              </span>
              <h2 class="text-2xl sm:text-[28px] font-serif font-bold text-on-primary-heading leading-tight tracking-tight">
                Great choice
              </h2>
              <p class="text-xs sm:text-sm text-on-primary-body leading-relaxed max-w-sm mt-1">
                Thank you for being part of the movement, and for giving this piece a home.
              </p>
            </div>

            <!-- Order Summary Card -->
            <div class="w-full bg-button-secondary/60 border border-on-primary-border rounded-[12px] p-3.5 sm:p-4 text-left flex flex-col gap-2.5 z-10">
              <div v-if="order?.customerEmail" class="flex items-center justify-between text-xs">
                <span class="text-on-primary-body">Confirmation sent to</span>
                <span class="font-medium text-on-primary-heading truncate max-w-[200px]">
                  {{ order.customerEmail }}
                </span>
              </div>

              <div v-if="order?.amount" class="flex items-center justify-between text-xs pt-2 border-t border-on-primary-border/60">
                <span class="text-on-primary-body font-medium">Total Paid</span>
                <span class="font-bold text-sm text-on-primary-heading">
                  {{ formatPrice(order.amount) }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="w-full flex flex-col gap-2 z-10 pt-1">
              <Button
                variant="primary"
                class="w-full py-2.5 text-sm flex items-center justify-center gap-2"
                @click="handleViewOrders"
              >
                <span>View My Orders</span>
                <Icon name="lucide:arrow-right" class="size-4" />
              </Button>

              <div class="flex items-center justify-center gap-4 pt-1">
                <button
                  type="button"
                  class="text-xs font-medium text-on-primary-body hover:text-on-primary-heading bg-transparent border-none cursor-pointer py-1"
                  @click="handleExploreArt"
                >
                  Explore More Art
                </button>
                <span class="text-on-primary-border">•</span>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 text-xs font-medium text-on-primary-body hover:text-on-primary-heading bg-transparent border-none cursor-pointer py-1"
                  @click="handleShare"
                >
                  <Icon name="lucide:share-2" class="size-3.5" />
                  <span>Share</span>
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.payment-modal-enter-active,
.payment-modal-leave-active {
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.payment-modal-enter-from,
.payment-modal-leave-to {
  opacity: 0;
}

.payment-modal-enter-from > div:last-child,
.payment-modal-leave-to > div:last-child {
  transform: scale(0.92) translateY(12px);
  opacity: 0;
}

.payment-modal-enter-to > div:last-child {
  transform: scale(1) translateY(0);
  opacity: 1;
}
</style>
